import {
  addDays,
  eachDayOfInterval,
  format,
  getISODay,
  parseISO,
} from 'date-fns'
import cloneDeep from 'lodash-es/cloneDeep'
import groupBy from 'lodash-es/groupBy'
import range from 'lodash-es/range'
import sortBy from 'lodash-es/sortBy'

import {
  CalendarAppointmentStatus,
  CalendarItemKind,
  CalendarSource,
} from '../../constants/calendar'
import { EMPTY_STRING } from '../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../constants/date'
import { PROFILE_ROUTE_NAMES } from '../../constants/route'
import { sleep } from '../../utils/async'
import { createDate, toUkTimezoneDate } from '../../utils/date'
import {
  getStorageSerializedItem,
  setStorageSerializedItem,
} from '../../utils/storage'

import { CALENDAR_PREFERENCES_INITIAL } from './Calendar.constants'
import {
  getIsCalendarAppointment,
  getIsCalendarAppointmentCancelled,
  getIsCalendarFollowUp,
  getIsCalendarTaskDeadline,
  getIsCalendarTaskGroup,
} from './Calendar.utils'

const CALENDAR_MOCK_LATENCY = 400

const CALENDAR_MOCK_TASK_TYPES_ENABLED = 2

const CALENDAR_MOCK_DAY_LIMIT = 3

const CALENDAR_MOCK_NEXT_FREE_DAY_SEARCH = 30

const CALENDAR_MOCK_DEADLINE_HOUR = 10

const CALENDAR_MOCK_ID_DATE_FORMAT = 'yyyyMMdd'

const CALENDAR_MOCK_ID_FACTOR = 100

const CALENDAR_MOCK_SPAN_LOOKBACK_DAYS = 7

const CALENDAR_MOCK_MIDNIGHT = [0, 0]

const CALENDAR_MOCK_PAGE = 1

const CALENDAR_MOCK_KIND_ORDER = {
  [CalendarItemKind.appointment]: 3,
  [CalendarItemKind.followUp]: 0,
  [CalendarItemKind.followUpDay]: 0,
  [CalendarItemKind.taskDeadline]: 2,
  [CalendarItemKind.taskGroup]: 1,
}

const CALENDAR_MOCK_KIND_SOURCE = {
  [CalendarItemKind.appointment]: CalendarSource.appointment,
  [CalendarItemKind.followUp]: CalendarSource.followUp,
  [CalendarItemKind.taskDeadline]: CalendarSource.taskDeadline,
}

const CALENDAR_MOCK_MOVED_ITEMS = new Map()

const CALENDAR_MOCK_PREFERENCES_STORAGE_KEY = 'bs_calendar_mock_preferences'

const CALENDAR_MOCK_BUILDING_BERGSTRASSE = {
  id: 310,
  name: 'Bergstraße 16',
  short_name: 'Bergstraße 16',
  url: PROFILE_ROUTE_NAMES.buildingProfileNG({ id: 310 }),
}

const CALENDAR_MOCK_BUILDING_LINDENWEG_4 = {
  id: 311,
  name: 'Lindenweg 4',
  short_name: 'Lindenweg 4',
  url: PROFILE_ROUTE_NAMES.buildingProfileNG({ id: 311 }),
}

const CALENDAR_MOCK_BUILDING_PARKALLEE = {
  id: 312,
  name: 'Parkallee 9',
  short_name: 'Parkallee 9',
  url: PROFILE_ROUTE_NAMES.buildingProfileNG({ id: 312 }),
}

const CALENDAR_MOCK_BUILDING_GARTENGASSE = {
  id: 313,
  name: 'Gartengasse 3',
  short_name: 'Gartengasse 3',
  url: PROFILE_ROUTE_NAMES.buildingProfileNG({ id: 313 }),
}

const CALENDAR_MOCK_BUILDING_LINDENWEG_3 = {
  id: 314,
  name: 'Lindenweg 3',
  short_name: 'Lindenweg 3',
  url: PROFILE_ROUTE_NAMES.buildingProfileNG({ id: 314 }),
}

const CALENDAR_MOCK_BUILDINGS = [
  CALENDAR_MOCK_BUILDING_BERGSTRASSE,
  CALENDAR_MOCK_BUILDING_GARTENGASSE,
  CALENDAR_MOCK_BUILDING_LINDENWEG_3,
  CALENDAR_MOCK_BUILDING_LINDENWEG_4,
  CALENDAR_MOCK_BUILDING_PARKALLEE,
]

const CALENDAR_MOCK_FLAT_TOP_12 = {
  id: 4471,
  name: 'Top 12',
  url: PROFILE_ROUTE_NAMES.flatProfileNG({ id: 4471 }),
}

const CALENDAR_MOCK_REQUEST_HEATING = {
  address: 'Bergstraße 16, 1030 Wien',
  building_id: 310,
  is_archived: false,
  number: 'SR-2841',
  title: 'Heating Bergstraße 16',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2841 }),
}

const CALENDAR_MOCK_REQUEST_WATER = {
  address: 'Lindenweg 4, 1040 Wien',
  building_id: 311,
  is_archived: false,
  number: 'SR-2903',
  title: 'Water damage Lindenweg 4',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2903 }),
}

const CALENDAR_MOCK_REQUEST_ELEVATOR = {
  address: 'Parkallee 9, 1050 Wien',
  building_id: 312,
  is_archived: false,
  number: 'SR-2710',
  title: 'Elevator maintenance Parkallee 9',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2710 }),
}

const CALENDAR_MOCK_REQUEST_ROOF = {
  address: 'Gartengasse 3, 1060 Wien',
  building_id: 313,
  is_archived: false,
  number: 'SR-2955',
  title: 'Roof leak Gartengasse 3',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2955 }),
}

const CALENDAR_MOCK_REQUEST_RENT = {
  address: 'Lindenweg 3, 1040 Wien',
  building_id: 314,
  is_archived: true,
  number: 'SR-2795',
  title: 'Rent increase Lindenweg 3',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2795 }),
}

const CALENDAR_MOCK_REQUEST_WINDOW = {
  address: 'Parkallee 9, 1050 Wien',
  building_id: 312,
  is_archived: false,
  number: 'SR-2968',
  title: 'Window repair Parkallee 9',
  url: PROFILE_ROUTE_NAMES.requestProfileNG({ id: 2968 }),
}

const CALENDAR_MOCK_ITEMS = [
  {
    assignees: [{ name: 'M. Huber' }],
    can_edit: true,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    weekday: 1,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 1,
    step_total: 6,
    title: 'Owner approval',
    weekday: 2,
  },
  {
    assignees: [{ name: 'S. Wagner' }],
    can_edit: false,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_ELEVATOR,
    weekday: 3,
  },
  {
    assignees: [{ name: 'M. Huber' }, { name: 'S. Wagner' }],
    can_edit: true,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_WATER,
    weekday: 3,
  },
  {
    assignees: [{ name: 'M. Huber' }],
    can_edit: true,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_RENT,
    weekday: 3,
  },
  {
    can_edit: true,
    done: true,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_WATER,
    step_index: 1,
    step_total: 4,
    title: 'Order drying',
    weekday: 3,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_WATER,
    step_index: 2,
    step_total: 4,
    title: 'Inform the expert',
    weekday: 3,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_WATER,
    step_index: 3,
    step_total: 4,
    title: 'Notify the insurance',
    weekday: 3,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 2,
    step_total: 6,
    title: 'Get a quote',
    weekday: 4,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 3,
    step_total: 6,
    title: 'Order the spare parts',
    weekday: 4,
  },
  {
    can_edit: true,
    done: true,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 4,
    step_total: 6,
    title: 'Book the technician',
    weekday: 4,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 5,
    step_total: 6,
    title: 'Inform the tenants',
    weekday: 4,
  },
  {
    assignees: [
      { name: 'M. Huber' },
      { name: 'S. Wagner' },
      { name: 'L. Koch' },
    ],
    can_edit: true,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_ROOF,
    weekday: 5,
  },
  {
    assignees: [{ name: 'L. Koch' }],
    can_edit: true,
    kind: CalendarItemKind.followUp,
    request: CALENDAR_MOCK_REQUEST_WINDOW,
    weekday: 5,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_WATER,
    step_index: 4,
    step_total: 4,
    title: 'Request an expert report',
    weekday: 5,
  },
  {
    can_edit: true,
    done: false,
    kind: CalendarItemKind.taskDeadline,
    request: CALENDAR_MOCK_REQUEST_HEATING,
    step_index: 6,
    step_total: 6,
    title: 'Confirm the repair date',
    weekday: 5,
  },
]

const CALENDAR_MOCK_APPOINTMENTS = [
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_BERGSTRASSE,
      flat: null,
      request: CALENDAR_MOCK_REQUEST_HEATING,
    },
    end: [10, 0],
    is_all_day: false,
    participant_count: 2,
    start: [9, 0],
    status: CalendarAppointmentStatus.confirmed,
    title: 'Boiler room inspection',
    weekday: 1,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_PARKALLEE,
      flat: null,
      request: null,
    },
    is_all_day: true,
    last_weekday: 2,
    participant_count: 6,
    status: CalendarAppointmentStatus.confirmed,
    title: 'Owners meeting',
    weekday: 2,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_LINDENWEG_4,
      flat: CALENDAR_MOCK_FLAT_TOP_12,
      request: CALENDAR_MOCK_REQUEST_WATER,
    },
    end: [12, 0],
    is_all_day: false,
    participant_count: 3,
    start: [11, 0],
    status: CalendarAppointmentStatus.confirmed,
    title: 'Key handover',
    weekday: 3,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_LINDENWEG_4,
      flat: null,
      request: CALENDAR_MOCK_REQUEST_WATER,
    },
    end: [12, 30],
    is_all_day: false,
    participant_count: 2,
    start: [11, 30],
    status: CalendarAppointmentStatus.confirmed,
    title: 'Expert visit',
    weekday: 3,
  },
  {
    anchor: { building: null, flat: null, request: null },
    end: [11, 30],
    is_all_day: false,
    participant_count: 1,
    start: [11, 0],
    status: CalendarAppointmentStatus.confirmed,
    title: 'Insurance call',
    weekday: 3,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_GARTENGASSE,
      flat: null,
      request: CALENDAR_MOCK_REQUEST_ROOF,
    },
    end: [15, 0],
    is_all_day: false,
    participant_count: 2,
    start: [14, 0],
    status: CalendarAppointmentStatus.cancelled,
    title: 'Roof inspection',
    weekday: 4,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_PARKALLEE,
      flat: null,
      request: CALENDAR_MOCK_REQUEST_ELEVATOR,
    },
    is_all_day: true,
    last_weekday: 5,
    participant_count: 2,
    status: CalendarAppointmentStatus.confirmed,
    title: 'Elevator shutdown',
    weekday: 4,
  },
  {
    anchor: {
      building: CALENDAR_MOCK_BUILDING_LINDENWEG_3,
      flat: null,
      request: CALENDAR_MOCK_REQUEST_RENT,
    },
    end: [11, 0],
    is_all_day: false,
    participant_count: 2,
    start: [10, 0],
    status: CalendarAppointmentStatus.confirmed,
    title: 'Rent increase talk',
    weekday: 5,
  },
]

function getIsCalendarMockItemOnDay({ day, item }) {
  return item.weekday === getISODay(day)
}

function getIsCalendarMockDue({ date, timezone }) {
  return (
    date <=
    format(toUkTimezoneDate(new Date(), timezone), DATE_API_DATA_FORMAT_SHORT)
  )
}

function getIsCalendarMockMoved(item) {
  return CALENDAR_MOCK_MOVED_ITEMS.has(item.id)
}

function getCalendarMockBuildingId(item) {
  return getIsCalendarAppointment(item)
    ? item.anchor.building?.id
    : item.request.building_id
}

function getIsCalendarMockShown({ filters, item }) {
  return (
    filters.sources.includes(CALENDAR_MOCK_KIND_SOURCE[item.kind]) &&
    (!filters.building_id ||
      getCalendarMockBuildingId(item) === filters.building_id) &&
    (filters.include_done_tasks || !item.done) &&
    (filters.include_cancelled_appointments ||
      !getIsCalendarAppointmentCancelled(item.status))
  )
}

function getCalendarMockKindOrder(item) {
  return CALENDAR_MOCK_KIND_ORDER[item.kind]
}

function getCalendarMockRequestNumber(item) {
  return item.request.number
}

function getCalendarMockGroupNumber(item) {
  return getIsCalendarTaskGroup(item)
    ? getCalendarMockRequestNumber(item)
    : EMPTY_STRING
}

function toCalendarMockSeedEntry({ date, day, index, item, timezone }) {
  return {
    ...item,
    date,
    deadline: createDate(
      timezone,
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      CALENDAR_MOCK_DEADLINE_HOUR
    ).toISOString(),
    id:
      Number(format(day, CALENDAR_MOCK_ID_DATE_FORMAT)) *
        CALENDAR_MOCK_ID_FACTOR +
      index,
  }
}

function getCalendarMockDayEntries({ day, timezone }) {
  const date = format(day, DATE_API_DATA_FORMAT_SHORT)

  const seedEntries = CALENDAR_MOCK_ITEMS.filter(item =>
    getIsCalendarMockItemOnDay({ day, item })
  )
    .map((item, index) =>
      toCalendarMockSeedEntry({ date, day, index, item, timezone })
    )
    .filter(entry => !getIsCalendarMockMoved(entry))

  const movedEntries = [...CALENDAR_MOCK_MOVED_ITEMS.values()].filter(
    entry => entry.date === date
  )

  return [...seedEntries, ...movedEntries].map(entry => ({
    ...entry,
    is_due: getIsCalendarMockDue({ date, timezone }),
  }))
}

function toCalendarMockTaskGroup({ date, deadlines }) {
  const [{ request }] = deadlines

  return {
    count: deadlines.length,
    date,
    id: `grp-${request.number}-${date}`,
    items: deadlines.map(
      ({ can_edit, deadline, done, id, step_index, step_total, title }) => ({
        can_edit,
        deadline,
        done,
        id,
        step_index,
        step_total,
        title,
      })
    ),
    kind: CalendarItemKind.taskGroup,
    request,
  }
}

function toCalendarMockTaskItems({ date, deadlines }) {
  return Object.values(
    groupBy(deadlines, deadline => deadline.request.number)
  ).map(requestDeadlines =>
    requestDeadlines.length > 1
      ? toCalendarMockTaskGroup({ date, deadlines: requestDeadlines })
      : requestDeadlines[0]
  )
}

function toCalendarMockFollowUpDay({ date, followUps }) {
  return {
    count: followUps.length,
    date,
    id: `fud-${date}`,
    items: sortBy(followUps, getCalendarMockRequestNumber),
    kind: CalendarItemKind.followUpDay,
  }
}

function toCalendarMockFollowUpItems({ date, followUps }) {
  return followUps.length > 1
    ? [toCalendarMockFollowUpDay({ date, followUps })]
    : followUps
}

function getCalendarMockDayItems({ day, filters, timezone }) {
  const date = format(day, DATE_API_DATA_FORMAT_SHORT)

  const entries = getCalendarMockDayEntries({ day, timezone }).filter(item =>
    getIsCalendarMockShown({ filters, item })
  )

  return sortBy(
    [
      ...toCalendarMockFollowUpItems({
        date,
        followUps: entries.filter(getIsCalendarFollowUp),
      }),
      ...toCalendarMockTaskItems({
        date,
        deadlines: entries.filter(getIsCalendarTaskDeadline),
      }),
    ],
    [
      getCalendarMockKindOrder,
      getCalendarMockGroupNumber,
      'deadline',
      'title',
      'id',
    ]
  )
}

function toCalendarMockDayLoad({ date, items }) {
  const followUpCount = items.filter(getIsCalendarFollowUp).length

  return {
    date,
    follow_up_count: followUpCount,
    is_full: followUpCount >= CALENDAR_MOCK_DAY_LIMIT,
    limit: CALENDAR_MOCK_DAY_LIMIT,
  }
}

function getCalendarMockDayLoad({ day, timezone }) {
  return toCalendarMockDayLoad({
    date: format(day, DATE_API_DATA_FORMAT_SHORT),
    items: getCalendarMockDayEntries({ day, timezone }),
  })
}

function getCalendarMockNextFreeDate({ date, timezone }) {
  const nextFreeDay = range(CALENDAR_MOCK_NEXT_FREE_DAY_SEARCH)
    .map(offset => addDays(parseISO(date), offset))
    .find(day => !getCalendarMockDayLoad({ day, timezone }).is_full)

  return nextFreeDay ? format(nextFreeDay, DATE_API_DATA_FORMAT_SHORT) : null
}

function getCalendarMockDateTime({ day, time, timezone }) {
  return createDate(
    timezone,
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    ...time
  ).toISOString()
}

function getCalendarMockLocalDate({ timezone, value }) {
  return format(toUkTimezoneDate(value, timezone), DATE_API_DATA_FORMAT_SHORT)
}

function getCalendarMockAppointmentEnd({ appointment, day, timezone }) {
  return appointment.is_all_day
    ? getCalendarMockDateTime({
        day: addDays(day, appointment.last_weekday - appointment.weekday),
        time: CALENDAR_MOCK_MIDNIGHT,
        timezone,
      })
    : getCalendarMockDateTime({ day, time: appointment.end, timezone })
}

function toCalendarMockAppointment({ appointment, day, index, timezone }) {
  const {
    anchor,
    is_all_day: isAllDay,
    participant_count,
    start,
    status,
    title,
  } = appointment

  return {
    anchor: {
      ...anchor,
      is_archived: Boolean(anchor.request?.is_archived),
      resident: null,
    },
    can_edit: true,
    end_at: getCalendarMockAppointmentEnd({ appointment, day, timezone }),
    id: `apt-${format(day, CALENDAR_MOCK_ID_DATE_FORMAT)}-${index}`,
    is_all_day: isAllDay,
    kind: CalendarItemKind.appointment,
    participant_count,
    start_at: getCalendarMockDateTime({
      day,
      time: isAllDay ? CALENDAR_MOCK_MIDNIGHT : start,
      timezone,
    }),
    status,
    title,
  }
}

function getIsCalendarMockAppointmentInRange({
  appointment,
  from,
  timezone,
  to,
}) {
  return (
    getCalendarMockLocalDate({ timezone, value: appointment.start_at }) <= to &&
    getCalendarMockLocalDate({ timezone, value: appointment.end_at }) >= from
  )
}

function getCalendarMockAppointments({ filters, from, timezone, to }) {
  const days = eachDayOfInterval({
    end: parseISO(to),
    start: addDays(parseISO(from), -CALENDAR_MOCK_SPAN_LOOKBACK_DAYS),
  })

  return sortBy(
    CALENDAR_MOCK_APPOINTMENTS.flatMap((appointment, index) =>
      days
        .filter(day => getIsCalendarMockItemOnDay({ day, item: appointment }))
        .map(day =>
          toCalendarMockAppointment({ appointment, day, index, timezone })
        )
    ).filter(
      appointment =>
        getIsCalendarMockAppointmentInRange({
          appointment,
          from,
          timezone,
          to,
        }) && getIsCalendarMockShown({ filters, item: appointment })
    ),
    appointment => appointment.start_at
  )
}

function toCalendarMockBuildingOption(building) {
  return { label: { title: building.name }, value: building.id }
}

function getIsCalendarMockBuildingFound({ building, search }) {
  return building.name.toLowerCase().includes(search.toLowerCase())
}

function getCalendarMockPreferencesStorageKey(userId) {
  return `${CALENDAR_MOCK_PREFERENCES_STORAGE_KEY}_${userId}`
}

function setCalendarMockMovedItems({ changes, ids, sourceDate, timezone }) {
  const sourceEntries = getCalendarMockDayEntries({
    day: parseISO(sourceDate),
    timezone,
  })

  ids
    .map(
      id =>
        CALENDAR_MOCK_MOVED_ITEMS.get(id) ??
        sourceEntries.find(entry => entry.id === id)
    )
    .filter(Boolean)
    .forEach(item => {
      CALENDAR_MOCK_MOVED_ITEMS.set(item.id, { ...item, ...changes })
    })
}

export function getMockCalendarItems({ from, timezone, to, ...filters }) {
  const days = eachDayOfInterval({ end: parseISO(to), start: parseISO(from) })

  return sleep(CALENDAR_MOCK_LATENCY).then(() => ({
    data: {
      day_load: filters.sources.includes(CalendarSource.followUp)
        ? days.map(day => getCalendarMockDayLoad({ day, timezone }))
        : [],
      items: [
        ...days.flatMap(day =>
          getCalendarMockDayItems({ day, filters, timezone })
        ),
        ...getCalendarMockAppointments({ filters, from, timezone, to }),
      ],
      settings_hint: { task_types_enabled: CALENDAR_MOCK_TASK_TYPES_ENABLED },
    },
  }))
}

export function getMockCalendarBuildings({ search = EMPTY_STRING }) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => ({
    data: {
      items: CALENDAR_MOCK_BUILDINGS.filter(building =>
        getIsCalendarMockBuildingFound({ building, search })
      ).map(toCalendarMockBuildingOption),
      meta: { curr_page: CALENDAR_MOCK_PAGE, page_count: CALENDAR_MOCK_PAGE },
    },
  }))
}

export function getMockCalendarPreferences(userId) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => ({
    data: getStorageSerializedItem(
      getCalendarMockPreferencesStorageKey(userId),
      CALENDAR_PREFERENCES_INITIAL
    ),
  }))
}

export function updateMockCalendarPreferences({ preferences, userId }) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => {
    setStorageSerializedItem(
      getCalendarMockPreferencesStorageKey(userId),
      preferences
    )

    return { data: cloneDeep(preferences) }
  })
}

export function getMockCalendarDayLoad({ date, timezone }) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => ({
    data: {
      ...getCalendarMockDayLoad({ day: parseISO(date), timezone }),
      next_free_date: getCalendarMockNextFreeDate({ date, timezone }),
    },
  }))
}

export function moveMockCalendarFollowUps({ date, ids, sourceDate, timezone }) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => {
    setCalendarMockMovedItems({ changes: { date }, ids, sourceDate, timezone })

    return {
      data: {
        day_load: getCalendarMockDayLoad({ day: parseISO(date), timezone }),
      },
    }
  })
}

export function updateMockCalendarTaskDeadline({
  deadline,
  id,
  sourceDate,
  timezone,
}) {
  return sleep(CALENDAR_MOCK_LATENCY).then(() => {
    setCalendarMockMovedItems({
      changes: {
        date: format(
          toUkTimezoneDate(deadline, timezone),
          DATE_API_DATA_FORMAT_SHORT
        ),
        deadline,
      },
      ids: [id],
      sourceDate,
      timezone,
    })

    return { data: { deadline, id } }
  })
}
